// Shared paint-layer boilerplate for the room dock's Canvas2D charts. Every
// *Draw.ts file used to hand-duplicate a canvas-sizing `setup()` and, for
// the log-frequency-axis charts, a grid+tick-label loop — this file is that
// code, extracted once the same ~30-40 lines had been copy-pasted into a
// 10th file. Geometry/tick math itself still lives in @roomtreatment/charts;
// this only owns the ctx calls.
import { canvasType, canvasTypeFont, withAlpha, type DesignToken } from '@roomtreatment/design';
import type {
  ChartPad, PlotDescriptor, PlotFrame, YAxisPadOptions, YAxisTickLabels,
} from '@roomtreatment/charts';
import {
  Y_AXIS_TICK_GAP,
  Y_AXIS_UNIT_CENTER_X,
  withFittedYAxisPad,
} from '@roomtreatment/charts';
import { canvasInkAt } from '../canvasInk';
import { themeOf } from '@/lib/theme/tokens';

export interface ChartCanvasSetup {
  ctx: CanvasRenderingContext2D;
  W: number;
  H: number;
}

// Axis text is set in the canvas type scale's roles (`canvasType` in
// @roomtreatment/design): tick numbers in `tick`, an axis's unit in `axis`,
// anything written inside the plot in `label`. The offsets below are sized
// for those roles — an 11px digit is ~8px tall, so its baseline sits 14px
// under the plot edge, and the unit row sits a clear 4px under that.

/** Pixel offset from plot bottom to the x-axis tick-number baseline. */
export const X_TICK_LABEL_Y_OFFSET = 14;
/** Pixel offset from plot bottom to the x-axis unit label baseline (below tick numbers). */
export const X_UNIT_LABEL_Y_OFFSET = 26;
/** The least bottom pad of a chart that names its x axis: the unit row plus
 *  clearance from the canvas edge. Below it the unit is clipped — clarity and
 *  decay-fit, at 20, painted their "Hz" 4px past the bottom of the canvas. */
export const X_AXIS_PAD_B = X_UNIT_LABEL_Y_OFFSET + 4;
/** X offset from plotX for right-aligned y tick numbers — re-exported from charts. */
export const Y_TICK_LABEL_X_OFFSET = -Y_AXIS_TICK_GAP;
/** Left-edge anchor for a rotated y-axis unit label — re-exported from charts. */
export const Y_UNIT_LABEL_X = Y_AXIS_UNIT_CENTER_X;
/** Baseline offset below a y gridline that centres a tick number on it —
 *  half an 11px digit's height. */
export const Y_TICK_LABEL_BASELINE = 4;

export interface AxisUnitLabelOpts {
  xLabel?: string;
  yLabel?: string;
  labelFill?: string;
}

/** Axis unit captions — separate from tick numbers so values never collide
 *  with Hz / dB / s / ms. X unit sits below the tick row; y unit is rotated
 *  −90° and anchored in the left margin. */
export function drawAxisUnitLabels(
  ctx: CanvasRenderingContext2D,
  frame: PlotFrame,
  opts: AxisUnitLabelOpts,
): void {
  const fill = opts.labelFill ?? canvasInkAt(ctx.canvas).label;
  if (opts.yLabel) {
    ctx.save();
    ctx.font = canvasTypeFont('axis');
    ctx.fillStyle = fill;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const cy = frame.plotY + frame.plotH / 2;
    ctx.translate(Y_UNIT_LABEL_X, cy);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(opts.yLabel, 0, 0);
    ctx.restore();
  }
  if (opts.xLabel) {
    ctx.save();
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';
    ctx.font = canvasTypeFont('axis');
    ctx.fillStyle = fill;
    ctx.fillText(opts.xLabel, frame.plotX + frame.plotW, frame.plotY + frame.plotH + X_UNIT_LABEL_Y_OFFSET);
    ctx.restore();
  }
}

/** Size the canvas's backing store to its CSS-measured element size (DPR-
 *  scaled, capped at 2x) and set the default tick-label font before any
 *  geometry call measures text against it. `minH` matches each chart's own
 *  minimum useful height — 120 for line/bar charts, 140 for the taller
 *  heatmap-style ones (waterfall, spectrogram, SPL uniformity).
 *
 *  Deliberately never fillRect()s a background: the canvas stays
 *  transparent so the dock wrap's `--ed-bg` CSS background shows through
 *  and every chart shares the viewport's exact ground in either theme.
 *  Don't add one. */
export function setupChartCanvas(canvas: HTMLCanvasElement, minH = 120): ChartCanvasSetup | null {
  const W = Math.max(200, Math.floor(canvas.clientWidth));
  const H = Math.max(minH, Math.floor(canvas.clientHeight));
  if (!W || !H) return null;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(dpr, dpr);
  ctx.font = canvasTypeFont('tick');
  return { ctx, W, H };
}

/**
 * Build a chart's geometry on a pad that fits its y-axis text — the widest
 * tick number measured at the tick font `setupChartCanvas` just set, plus the
 * rotated unit caption beside it (`withFittedYAxisPad` in @roomtreatment/charts
 * owns the arithmetic). Call it straight after setup, before any other font
 * is set on the context. The frame it returns is the one the drawer paints
 * AND hands to the chart shell, so the cursor inverts through the fitted pad,
 * never the nominal one.
 */
export function fitYAxisPad<G>(
  ctx: CanvasRenderingContext2D,
  pad: ChartPad,
  build: (pad: ChartPad) => G,
  labels: (geo: G) => YAxisTickLabels,
  opts?: YAxisPadOptions,
): G {
  return withFittedYAxisPad(pad, (label) => ctx.measureText(label).width, build, labels, opts);
}

export interface LogFreqAxisTicks {
  all: { x: number; major: boolean }[];
  labeled: { x: number; label: string }[];
}
export interface LinearAxisTick {
  y: number;
  major: boolean;
  label: string;
}

/** One series in a line-style legend: its name and the stroke it is drawn with. */
export interface LineLegendEntry {
  label: string;
  dash: number[];
  width: number;
}

const LEGEND_SAMPLE_W = 14;
const LEGEND_SAMPLE_GAP = 3;
const LEGEND_ENTRY_GAP = 10;
/** Row pitch when the legend wraps — the label role's size plus air. */
const LEGEND_ROW_H = canvasType.label.size + 4;

/**
 * The in-plot legend every multi-series chart draws (decay-fit methods,
 * clarity metrics, per-speaker traces): a stroke sample and the series' name,
 * top-left inside the plot, in the canvas `label` role. Series are told apart
 * by dash and weight, never hue, so the sample IS the key.
 *
 * Wraps onto a further row instead of running past the plot's right edge —
 * five speakers' names at a readable size do not fit on one line of a
 * 300px dock chart.
 */
export function drawLineLegend(
  ctx: CanvasRenderingContext2D,
  frame: PlotFrame,
  entries: readonly LineLegendEntry[],
  ink: { line: string; label: string },
): void {
  ctx.save();
  ctx.font = canvasTypeFont('label');
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const x0 = frame.plotX + 6;
  const right = frame.plotX + frame.plotW - 4;
  let lx = x0;
  let ly = frame.plotY + 9;
  for (const e of entries) {
    const w = LEGEND_SAMPLE_W + LEGEND_SAMPLE_GAP + ctx.measureText(e.label).width;
    if (lx > x0 && lx + w > right) {
      lx = x0;
      ly += LEGEND_ROW_H;
    }
    ctx.setLineDash(e.dash);
    ctx.strokeStyle = ink.line;
    ctx.lineWidth = e.width;
    ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(lx + LEGEND_SAMPLE_W, ly); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = ink.label;
    // Baseline half a cap height below the sample, so the name sits centred
    // on its stroke.
    ctx.fillText(e.label, lx + LEGEND_SAMPLE_W + LEGEND_SAMPLE_GAP, ly + 4);
    lx += w + LEGEND_ENTRY_GAP;
  }
  ctx.restore();
}

/** Log-frequency x-axis + linear y-axis grid lines and tick labels — the
 *  frame every log-freq line/bar chart in this dock draws before its own
 *  series (freq response, RT60, RTA, modal density, phase, group delay,
 *  per-speaker FR/phase, clarity, decay-fit comparison).
 *
 *  `xDistinguishMajor`/`yDistinguishMajor` (both default true) control
 *  whether major gridlines get `gridMajor`'s heavier weight or fall back to
 *  `gridMinor` like every other line — clarity/decay-fit charts pass false
 *  on x (and decay-fit on y too) to keep their existing flat-weight grid; do
 *  not "fix" that to true, it is that chart's deliberate look, not a bug. */
export function drawLogFreqAxisGrid(
  ctx: CanvasRenderingContext2D,
  frame: PlotFrame,
  xTicks: LogFreqAxisTicks,
  yTicks: LinearAxisTick[],
  opts: {
    gridMajor: string;
    gridMinor: string;
    xDistinguishMajor?: boolean;
    yDistinguishMajor?: boolean;
    xLabel?: string;
    yLabel?: string;
  },
): void {
  const { gridMajor, gridMinor, xDistinguishMajor = true, yDistinguishMajor = true, xLabel, yLabel } = opts;
  const label = canvasInkAt(ctx.canvas).label;
  ctx.textAlign = 'right';
  for (const t of yTicks) {
    const y = frame.plotY + t.y;
    ctx.strokeStyle = yDistinguishMajor && t.major ? gridMajor : gridMinor;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(frame.plotX, y); ctx.lineTo(frame.plotX + frame.plotW, y); ctx.stroke();
    ctx.fillStyle = label;
    ctx.fillText(t.label, frame.plotX + Y_TICK_LABEL_X_OFFSET, y + Y_TICK_LABEL_BASELINE);
  }
  for (const t of xTicks.all) {
    const x = frame.plotX + t.x;
    ctx.strokeStyle = xDistinguishMajor && t.major ? gridMajor : gridMinor;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x, frame.plotY); ctx.lineTo(x, frame.plotY + frame.plotH); ctx.stroke();
  }
  ctx.textAlign = 'center';
  ctx.fillStyle = label;
  for (const t of xTicks.labeled) {
    ctx.fillText(t.label, frame.plotX + t.x, frame.plotY + frame.plotH + X_TICK_LABEL_Y_OFFSET);
  }

  drawAxisUnitLabels(ctx, frame, { xLabel, yLabel });
}

/**
 * What a drawer hands back to the chart shell: the caption to print under
 * the canvas, and a description of the plot it just painted (the plot box
 * plus the x/y scales it laid the data out with). The shell draws the
 * crosshair and the standing scrubber line from `plot`, so the readout is
 * inverted through the exact scales the curve was drawn with — never a
 * second copy of the pad or the axis range that could drift. `plot` is null
 * when nothing was drawn (empty data), and may omit `y` for a chart with no
 * readable y axis (waterfall cascade, spectrogram band rows).
 */
export interface ChartPaint {
  caption: string;
  plot: PlotDescriptor | null;
}

/**
 * The chart grid, in the theme's ink. Major and minor weights are the SAME
 * two alphas on every log-frequency chart in the dock — one definition here
 * rather than a pair of literals repeated in each drawer, so a grid that
 * needs to be quieter is one edit, not ten.
 */
export function chartGridInk(t: Record<DesignToken, string>) {
  return {
    gridMajor: withAlpha(t.ink, 0.14),
    gridMinor: withAlpha(t.ink, 0.06),
  };
}

/**
 * Caption for a chart that draws two curves in the one-hue before/after
 * encoding (full-weight ink over the faint tier, see chartDraw.ts).
 *
 * Named for what the reader SEES, not for what the drawing code calls it.
 * "Solid" and "faint" describe the ink to whoever wrote the drawer; to someone
 * looking at two lines they say nothing about which is which. What they see
 * depends on the theme: full-weight ink is black on paper and white on navy,
 * so the caption asks the canvas it describes. Every chart that paints the
 * pair prints this.
 */
export function inkLegend(canvas: Element, black: string, gray: string): string {
  const solid = themeOf(canvas) === 'dark' ? 'White' : 'Black';
  return `${solid}: ${black} · Gray: ${gray}`;
}

export const EMPTY_PAINT: ChartPaint = Object.freeze({ caption: '', plot: null });
